/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Gift } from 'lucide-react';
import { applyMask } from '../../utils/masks';
import { ReferralModal } from '../modals/modal-code';
import StepContainer from '../StepContainer';
import { REFERRAL_ENABLED } from '../../config/features';
import { PrecoDoRodape } from '../TermosAssinatura';
import type { RodapeDoPreco } from '../../utils/termos-assinatura';

interface StepOneProps {
  onSubmit: (data: { name: string; whatsapp: string; referralCode: string | null }) => void;
  disabled: boolean;
  price: {
    total: string;
    cupom: any;
    /** Valor + frequência/total da barra do celular (utils/termos-assinatura). */
    rodape?: RodapeDoPreco;
  };
  requireTurnstile?: boolean;
  turnstileToken?: string;
  referralCode: string | null;
  onReferralChange: (code: string | null) => void;
  initialName?: string;
  initialWhatsapp?: string;
}

const StepOne: React.FC<StepOneProps> = ({
  onSubmit,
  disabled,
  price,
  requireTurnstile,
  turnstileToken,
  referralCode,
  onReferralChange,
  initialName,
  initialWhatsapp,
}) => {
  const [loading, setLoading] = useState(false);
  const isSubmittingRef = useRef(false); // 🔒 Proteção contra duplo clique
  const [name, setName] = useState(initialName ?? '');
  const [whatsapp, setWhatsapp] = useState(initialWhatsapp ?? '');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    whatsapp?: string;
  }>({});
  const [touched, setTouched] = useState<{
    name: boolean;
    whatsapp: boolean;
  }>({
    name: false,
    whatsapp: false,
  });
  function handleConfirm(code: string) {
    const isValid = /^[A-Z0-9]{5}-\d{4}$/.test(code);
    if (isValid) {
      onReferralChange(code);
      setIsModalOpen(false);
    } else {
      alert('Código inválido. Por favor, verifique e tente novamente.');
    }
  }

  const validateName = (name: string): boolean => {
    return name.trim().length >= 3 && /^[a-zA-ZÀ-ÿ\s]+$/.test(name.trim());
  };

  const validateWhatsapp = (whatsapp: string): boolean => {
    return whatsapp.replace(/\D/g, '').length >= 10;
  };

  const validateField = (field: string, value: string): string | undefined => {
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Nome é obrigatório';
        if (value.trim().length < 3) return 'Nome deve ter pelo menos 3 caracteres';
        if (!/^[a-zA-ZÀ-ÿ\s]+$/.test(value.trim())) return 'Nome deve conter apenas letras';
        break;
      case 'whatsapp':
        if (!value.trim()) return 'WhatsApp é obrigatório';
        if (value.replace(/\D/g, '').length < 10) return 'WhatsApp inválido';
        break;
    }
    return undefined;
  };

  const isNameValid = validateName(name);
  const isWhatsappValid = validateWhatsapp(whatsapp);
  const isFormValid = isNameValid && isWhatsappValid && (!requireTurnstile || !!turnstileToken);

  const handleFieldChange = (field: string, value: string) => {
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const value = field === 'name' ? name : whatsapp;
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const getFieldError = (field: string): string | undefined => {
    return touched[field as keyof typeof touched] ? errors[field as keyof typeof errors] : undefined;
  };

  const getFieldClassName = (field: string): string => {
    const hasError = getFieldError(field);
    const isTouched = touched[field as keyof typeof touched];
    const baseClass = 'border px-4 py-4 rounded-lg h-12';

    if (hasError && isTouched) {
      return `${baseClass} border-red-500 bg-red-50`;
    } else if (isTouched && !hasError) {
      return `${baseClass} border-green-500 bg-green-50`;
    }
    return `${baseClass} border-[#dadae5]`;
  };

  return (
    <>
      <StepContainer>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h1 className="font-bold text-xl mb-3">Dados do responsável</h1>
          <span className="text-base text-[#595D69]">
            Preencha seus dados para criarmos sua assinatura e garantirmos que o pacote chegue até você.
          </span>
        </motion.div>

        <motion.div
          className="py-3 flex flex-col w-full mt-5 gap-2"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <label className="text-base text-[#595D69]">Nome completo</label>
          <input
            className={getFieldClassName('name')}
            placeholder="Nome e Sobrenome"
            type="text"
            name="name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              handleFieldChange('name', e.target.value);
            }}
            onBlur={() => handleFieldBlur('name')}
            aria-describedby={getFieldError('name') ? 'name-error' : undefined}
            aria-invalid={!!getFieldError('name')}
          />
          {getFieldError('name') && (
            <motion.p
              id="name-error"
              className="text-red-500 text-sm mt-1"
              role="alert"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              {getFieldError('name')}
            </motion.p>
          )}
        </motion.div>

        <motion.div
          className="py-3 flex flex-col w-full gap-2"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <label className="text-base text-[#595D69]">Whatsapp</label>
          <input
            name="whatsapp"
            type="text"
            inputMode="tel"
            maxLength={16}
            className={getFieldClassName('whatsapp')}
            placeholder="(00) 0 0000-0000"
            value={whatsapp}
            onChange={(e) => {
              const masked = applyMask(e.target.value, 'phone');
              setWhatsapp(masked);
              handleFieldChange('whatsapp', masked);
            }}
            onBlur={() => handleFieldBlur('whatsapp')}
            aria-describedby={getFieldError('whatsapp') ? 'whatsapp-error' : undefined}
            aria-invalid={!!getFieldError('whatsapp')}
          />
          {getFieldError('whatsapp') && (
            <motion.p
              id="whatsapp-error"
              className="text-red-500 text-sm mt-1"
              role="alert"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              {getFieldError('whatsapp')}
            </motion.p>
          )}
        </motion.div>

        {/* Gate de build (#128): default ligado; VITE_REFERRAL_ENABLED=false esconde. */}
        {REFERRAL_ENABLED && (
          <div className="flex px-5 py-5 flex-col gap-3 mt-5 border border-[#dadae5] rounded-lg">
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#018ED5] shrink-0" />
              <h2>Alguém te indicou o Clubinho?</h2>
            </div>
            <span className="text-base text-[#595D69]">
              Coloque o código aqui e vocês dois ganham. Você recebe um presente no seu primeiro pacote e quem te
              indicou também é presenteado.
            </span>

            {referralCode ? (
              <div className="mt-2 flex items-center justify-between border border-black/10 rounded-md p-3 bg-gray-50">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Código de indicação</label>
                  <span className="font-semibold text-gray-800">{referralCode}</span>
                  <p className="text-sm text-green-600 mt-1">Presente garantido no seu primeiro pacote 🎁</p>
                </div>
                <button onClick={() => onReferralChange(null)} className="text-gray-400 hover:text-red-500 text-xl">
                  &times;
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsModalOpen(true)}
                className="hover:opacity-90 hover:cursor-pointer flex items-center justify-start gap-2 text-[#018ED5]"
              >
                <img src="/icon-plus-blue.svg" alt="" /> Adicionar código
              </button>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-10">
          <button
            disabled
            className="disabled:opacity-50 disabled:cursor-not-allowed flex gap-2 items-center justify-start hover:opacity-90 hover:cursor-pointer text-[#9195A1] px-3 py-2 rounded-md"
          >
            <img src="/arrow-left.svg" alt="" /> Voltar
          </button>
          <button
            disabled={!isFormValid || disabled || loading}
            onClick={async () => {
              // 🔒 Proteção contra duplo clique ANTES do React atualizar
              if (isSubmittingRef.current) {
                console.warn('⚠️ Duplo clique bloqueado!');
                return;
              }

              isSubmittingRef.current = true;
              setLoading(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
              try {
                await onSubmit({
                  name,
                  whatsapp,
                  referralCode,
                });
              } catch (error) {
                console.error('Erro no StepOne:', error);
              } finally {
                setLoading(false);
                isSubmittingRef.current = false;
              }
            }}
            className={`items-center hidden lg:flex justify-center gap-2 text-white px-3 py-2 rounded-md transition-all duration-300 ${
              isFormValid && !disabled ? 'bg-[#018ED5] hover:opacity-90' : 'bg-[#dadae5] cursor-not-allowed'
            }`}
          >
            {loading && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {loading ? 'Salvando...' : 'Continuar'}
          </button>
        </div>
      </StepContainer>

      <ReferralModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={handleConfirm} />

      <div className="w-full lg:hidden flex items-center justify-between gap-3 px-4 py-2 left-0 min-h-[80px] z-50 bg-white border-t border-black/10 fixed bottom-0">
        <div className="min-w-0 flex-1">
          <span>
            {price.cupom && (
              <>
                <img src="/icon-tag.svg" className="w-4 h-4" /> {price.cupom.code}
              </>
            )}
          </span>
          {price.rodape ? (
            <PrecoDoRodape rodape={price.rodape} />
          ) : (
            <h3 className="font-bold text-3xl">{price.total}</h3>
          )}
          <h2 className="font-bold text-xs">+ FRETE GRÁTIS</h2>
        </div>
        <div className="shrink-0 max-w-[50%]">
          <button
            className={`flex items-center justify-center gap-2 text-white px-4 py-4 text-center leading-tight rounded-md transition-all duration-300 ${
              isFormValid && !disabled ? 'bg-[#018ED5] hover:opacity-90' : 'bg-[#dadae5] cursor-not-allowed'
            }`}
            onClick={async () => {
              // 🔒 Proteção contra duplo clique ANTES do React atualizar
              if (isSubmittingRef.current) {
                console.warn('⚠️ Duplo clique bloqueado!');
                return;
              }

              isSubmittingRef.current = true;
              setLoading(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
              try {
                await onSubmit({
                  name,
                  whatsapp,
                  referralCode,
                });
              } catch (error) {
                console.error('Erro no StepOne:', error);
              } finally {
                setLoading(false);
                isSubmittingRef.current = false;
              }
            }}
          >
            {loading && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {loading ? 'Salvando...' : 'Continuar'}
          </button>
        </div>
      </div>
    </>
  );
};

export default StepOne;
